import React, { createContext, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';

/**
 * Socket Context
 * Provides socket.io connection throughout the application
 */
export const SocketContext = createContext(null);

/**
 * Socket Provider Component
 */
export const SocketProvider = ({ children, url, options = {} }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get auth token from localStorage or other storage
    const token = localStorage.getItem('token');

    // Create socket connection
    const socketInstance = io(url || process.env.REACT_APP_SOCKET_URL || window.location.origin, {
      transports: ['websocket', 'polling'],
      auth: {
        token
      },
      ...options
    });

    // Connection events
    socketInstance.on('connect', () => {
      console.log('Socket connected:', socketInstance.id);
      setIsConnected(true);
      setError(null);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (err) => {
      console.error('Socket connection error:', err);
      setError(err.message);
      setIsConnected(false);
    });

    socketInstance.on('error', (err) => {
      console.error('Socket error:', err);
      setError(err.message);
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      socketInstance.disconnect();
    };
  }, [url, options]);

  // Reconnect function
  const reconnect = useCallback(() => {
    if (socket) {
      socket.connect();
    }
  }, [socket]);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
    }
  }, [socket]);

  const value = {
    socket,
    isConnected,
    error,
    reconnect,
    disconnect
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
